import type { AgentStep, Visualization } from "@unbox/api";
import { useTick } from "../hooks/useTick";
import { useAskSurface } from "../lib/ask-surface";
import { type DisplayKind, pendingDisplay, pillStatus, type StreamingPhase, streamStatus } from "../lib/stream-status";
import { UnboxMark } from "./UnboxMark";

type StreamingAnswer = {
  /** True from the question being sent until the result lands or the user stops it. */
  streaming: boolean;
  agentSteps?: AgentStep[];
  currentStepLabel?: string;
  streamingText?: string;
  streamingPhase?: StreamingPhase;
  streamingDisplay?: Visualization | null;
  reportRun?: boolean;
  stopped?: boolean;
};

/**
 * What to say while an answer streams, worded for where it renders, and which display
 * to hold space for. The drawer names the query's topic before the model writes; the
 * page names only the part of the answer being prepared.
 */
export function useAnswerStatus(answer: StreamingAnswer): { label: string | null; skeleton: DisplayKind | null } {
  const drawer = useAskSurface() === "drawer";
  const displayArrived = answer.streamingDisplay !== undefined;
  const state = {
    stepRunning: !!answer.currentStepLabel,
    hasSteps: (answer.agentSteps?.length ?? 0) > 0,
    hasText: !!answer.streamingText,
    phase: answer.streamingPhase,
    displayArrived,
    stopped: answer.stopped,
  };
  const tick = useTick(drawer && answer.streaming && !state.stepRunning && !state.hasSteps && !state.hasText);

  let label: string | null = null;
  if (answer.streaming) {
    label = drawer
      ? pillStatus({
          ...state,
          stepLabel: answer.currentStepLabel ?? answer.agentSteps?.at(-1)?.label,
          tick,
          labelAsWritten: answer.reportRun,
        })
      : streamStatus(state);
  }
  return { label, skeleton: pendingDisplay(answer.streamingPhase, displayArrived) };
}

export function AnswerStatus({ label }: { label: string | null }) {
  const drawer = useAskSurface() === "drawer";
  if (!label) return null;
  if (drawer) {
    return (
      <div role="status" aria-live="polite" className="ask-status-pill">
        <UnboxMark size={14} motion="spin" className="text-[#d6bbfb]" />
        {/* Keyed on the phrase so each new one plays its entrance. */}
        <span key={label} className="ask-status-text">
          {label}
        </span>
      </div>
    );
  }
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "2px 0 12px" }}
    >
      <span className="ask-stream-dot" aria-hidden="true" />
      <span className="ask-stream-text">{label}</span>
    </div>
  );
}
